import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import farmerService from '../../services/farmer.service';
import { useNavigate } from 'react-router-dom';
import Toast from '../../components/Toast/Toast';
import './ViewFarmers.css';

const ViewFarmers = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState(null);

  // Confirm delete modal
  const [deleteState, setDeleteState] = useState({
    open: false,
    farmer: null,
    loading: false,
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
useEffect(() => {
  console.log('AuthContext debug:', { user, isSuperAdmin });
}, [user, isSuperAdmin]);

  useEffect(() => {
    fetchFarmers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, searchTerm, isSuperAdmin]);

  const fetchFarmers = async () => {
    setLoading(true);
    setError(null);
    try {
      // ✅ SA needs the selected company; others use JWT companyId
      const cid = isSuperAdmin ? localStorage.getItem('selectedCompanyId') : null;
      if (isSuperAdmin && !cid) {
        setError('Please select a company first from the navbar');
        setFarmers([]);
        setLoading(false);
        return;
      }

      const response = await farmerService.getAllFarmers(
        pagination.page,
        pagination.limit,
        searchTerm,
        cid
      );

      if (response.success) {
        setFarmers(response.data.farmers);
        setPagination(prev => ({
          ...prev,
          ...response.data.pagination,
        }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch farmers');
      console.error('Error fetching farmers:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Delete flow with modal ───────────────────────────────────────────────
  const askDelete = (farmer) => {
    setDeleteState({ open: true, farmer, loading: false });
  };

  const confirmDelete = async () => {
    setDeleteState(prev => ({ ...prev, loading: true }));
    try {
      const response = await farmerService.deleteFarmer(deleteState.farmer.id);
      if (response.success) {
        setToast({ message: 'Farmer deleted successfully', type: 'success' });
        fetchFarmers();
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to delete farmer',
        type: 'error',
      });
    } finally {
      setDeleteState({ open: false, farmer: null, loading: false });
    }
  };

  const cancelDelete = () => {
    if (deleteState.loading) return;
    setDeleteState({ open: false, farmer: null, loading: false });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className="view-farmers-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="farmers-header">
        <h4 className="farmers-title">View Farmers</h4>
        <button onClick={() => navigate('/farmer/add')}>Add Farmer</button>
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search farmers by name, village, or mobile..."
          className="search-input"
          value={searchTerm}
          onChange={handleSearch}
        />
        <span className="search-icon">🔍</span>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading farmers...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="btn-retry" onClick={fetchFarmers}>Retry</button>
        </div>
      ) : farmers.length === 0 ? (
        <div className="empty-state">
          <p>No farmers found</p>
          <button className="btn-add-farmer" onClick={() => navigate('/farmer/add')}>
            Add Your First Farmer
          </button>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="farmers-table">
              <thead>
                <tr>
                  <th>FARMER NAME</th>
                  <th>HEADQUARTER</th>
                  <th>VILLAGE</th>
                  <th>TEHSIL</th>
                  <th>DISTRICT</th>
                  <th>MOBILE NUMBER</th>
                  <th>CROP</th>
                  <th>ACRE</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {farmers.map((farmer) => (
                  <tr key={farmer.id}>
                    <td>{farmer.farmerName}</td>
                    <td>{farmer.headquarter?.name || '—'}</td>
                    <td>{farmer.villageRef?.name || farmer.village || '—'}</td>
                    <td>{farmer.villageRef?.talukaName || '—'}</td>
                    <td>{farmer.villageRef?.districtName || '—'}</td>
                    <td>{farmer.mobileNo}</td>
                    <td>{farmer.crop || '—'}</td>
                    <td>{farmer.acre || '—'}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-action btn-view"
                          onClick={() => navigate(`/farmer/view/${farmer.id}`)}>
                          View
                        </button>
                        <button className="btn-action btn-edit"
                          onClick={() => navigate(`/farmer/edit/${farmer.id}`)}>
                          Edit
                        </button>
                        <button className="btn-action btn-delete"
                          onClick={() => askDelete(farmer)}
                          style={{ background: '#fce4ec', color: '#c62828' }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button className="pagination-btn"
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}>
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </span>
            <button className="pagination-btn"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}>
              Next
            </button>
          </div>
        </>
      )}

      {/* Delete confirmation modal */}
      {deleteState.open && (
        <div
          onClick={cancelDelete}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12,
              padding: 24, maxWidth: 400, width: '100%',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            }}
          >
            <h4 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1a1a' }}>
              Delete Farmer
            </h4>
            <p style={{ margin: '12px 0 20px 0', fontSize: 14, color: '#495057', lineHeight: 1.5 }}>
              Are you sure you want to delete <strong>{deleteState.farmer?.farmerName}</strong>?
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={cancelDelete}
                disabled={deleteState.loading}
                style={{
                  padding: '10px 18px', borderRadius: 8, border: '1px solid #d1d5db',
                  background: '#fff', color: '#495057', fontWeight: 600, fontSize: 14,
                  cursor: deleteState.loading ? 'not-allowed' : 'pointer',
                }}
              >Cancel</button>
              <button
                onClick={confirmDelete}
                disabled={deleteState.loading}
                style={{
                  padding: '10px 18px', borderRadius: 8, border: 'none',
                  background: '#c62828', color: '#fff', fontWeight: 600, fontSize: 14,
                  cursor: deleteState.loading ? 'not-allowed' : 'pointer',
                  opacity: deleteState.loading ? 0.7 : 1,
                }}
              >
                {deleteState.loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewFarmers;